import React, { useEffect, useState } from 'react';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { syncService } from '../services/syncService';

interface OfflineIndicatorProps {
  className?: string;
}

export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({ className = '' }) => {
  const { isOnline, wasOffline } = useOnlineStatus();
  const [syncStatus, setSyncStatus] = useState({
    pendingActionsCount: 0,
    isSyncing: false,
  });

  useEffect(() => {
    const updateSyncStatus = async () => {
      const status = await syncService.getSyncStatus();
      setSyncStatus({
        pendingActionsCount: status.pendingActionsCount,
        isSyncing: status.isSyncing,
      });
    };

    updateSyncStatus();

    // Only poll when offline or have pending actions to reduce battery drain
    // When online with no pending actions, no need to keep polling
    if (!isOnline || syncStatus.pendingActionsCount > 0 || syncStatus.isSyncing) {
      const interval = setInterval(updateSyncStatus, 5000);
      return () => clearInterval(interval);
    }
  }, [isOnline, syncStatus.pendingActionsCount, syncStatus.isSyncing]);

  // Don't show anything if online and no pending actions
  if (isOnline && !wasOffline && syncStatus.pendingActionsCount === 0) {
    return null;
  }

  return (
    <div data-testid="offline-indicator" className={`offline-indicator ${className}`}>
      {!isOnline && (
        <div className="offline-banner offline">
          <span className="icon">📴</span>
          <div className="content">
            <strong>You're offline</strong>
            <p>Changes will sync when you're back online</p>
          </div>
        </div>
      )}

      {isOnline && wasOffline && (
        <div className="offline-banner online">
          <span className="icon">✓</span>
          <div className="content">
            <strong>Back online</strong>
            <p>Syncing your changes...</p>
          </div>
        </div>
      )}

      {isOnline && syncStatus.pendingActionsCount > 0 && !wasOffline && (
        <div className="offline-banner syncing">
          <span className="icon">{syncStatus.isSyncing ? '⟳' : '⏸'}</span>
          <div className="content">
            <strong>
              {syncStatus.isSyncing ? 'Syncing...' : 'Pending changes'}
            </strong>
            <p>
              {syncStatus.pendingActionsCount} action
              {syncStatus.pendingActionsCount !== 1 ? 's' : ''} waiting
            </p>
          </div>
        </div>
      )}

      <style>{`
        .offline-indicator {
          position: fixed;
          bottom: 80px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 10000;
          pointer-events: none;
          width: auto;
          max-width: 90%;
        }

        .offline-banner {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 20px;
          background-color: #2C2C2E;
          border-radius: 12px;
          animation: slideUp 0.3s ease-out;
          pointer-events: auto;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
          min-width: 280px;
        }

        .offline-banner.offline {
          background-color: #FF3B30;
          color: white;
        }

        .offline-banner.online {
          background-color: #34C759;
          color: white;
          animation: slideUp 0.3s ease-out, fadeOut 0.3s ease-out 2.7s forwards;
        }

        .offline-banner.syncing {
          background-color: #FF9500;
          color: white;
        }

        .offline-banner .icon {
          font-size: 20px;
          flex-shrink: 0;
        }

        .offline-banner.syncing .icon {
          animation: rotate 1s linear infinite;
        }

        .offline-banner .content {
          flex: 1;
          min-width: 0;
        }

        .offline-banner strong {
          display: block;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 2px;
        }

        .offline-banner p {
          margin: 0;
          font-size: 12px;
          opacity: 0.9;
        }

        @keyframes slideUp {
          from {
            transform: translateY(120%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes fadeOut {
          from {
            opacity: 1;
            transform: translateY(0);
          }
          to {
            opacity: 0;
            transform: translateY(120%);
          }
        }

        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        /* Mobile adjustments */
        @media (max-width: 640px) {
          .offline-indicator {
            bottom: 70px;
            max-width: 85%;
          }

          .offline-banner {
            padding: 10px 16px;
            min-width: 250px;
          }

          .offline-banner .icon {
            font-size: 18px;
          }

          .offline-banner strong {
            font-size: 13px;
          }

          .offline-banner p {
            font-size: 11px;
          }
        }

        /* Very small screens */
        @media (max-width: 400px) {
          .offline-indicator {
            bottom: 65px;
            max-width: 92%;
          }

          .offline-banner {
            min-width: auto;
            padding: 10px 14px;
          }
        }
      `}</style>
    </div>
  );
};

export default OfflineIndicator;
